"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trackOwnedAnalyticsOnly } from "@/lib/analytics-events";
import { trackExperimentExposure } from "@/lib/experiment";
import type { PanchangResponse } from "@/lib/api/panchang";
import type { TithiLanguage, TithiPurposeId } from "./tithi-today.copy";

export const DAILY_TIMING_EXPERIMENT_ID = "daily_timing_v2_pilot_2026_07";
export const DAILY_TIMING_TREATMENT_ARM = "daily_timing_v2";

export interface DailyTimingTelemetry {
  answerRendered: (result: PanchangResponse, citySlug: string) => void;
  purposeSelected: (purpose: TithiPurposeId) => void;
  primaryCtaAudited: (count: number, purpose?: TithiPurposeId) => void;
}

function useFireOnce(language: TithiLanguage, sourcePath: string, arm: string | null) {
  const emitted = useRef(new Set<string>());
  return useCallback(
    (event: string, key: string, properties: Record<string, unknown> = {}) => {
      if (!arm || emitted.current.has(`${event}:${key}`)) return;
      emitted.current.add(`${event}:${key}`);
      trackOwnedAnalyticsOnly(event, {
        experiment_id: DAILY_TIMING_EXPERIMENT_ID,
        variant: arm,
        language,
        source_path: sourcePath,
        ...properties,
      });
    },
    [arm, language, sourcePath]
  );
}

export function useDailyTimingExperiment(language: TithiLanguage, sourcePath: string) {
  const [arm, setArm] = useState<string | null>(null);
  const emit = useFireOnce(language, sourcePath, arm);

  useEffect(() => {
    let mounted = true;
    queueMicrotask(() => {
      if (mounted) setArm(trackExperimentExposure(DAILY_TIMING_EXPERIMENT_ID));
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    emit("daily_timing_surface_entered", `${language}:${sourcePath}`);
  }, [emit, language, sourcePath]);

  const telemetry = useMemo<DailyTimingTelemetry>(
    () => ({
      answerRendered(result, citySlug) {
        emit("daily_timing_answer_rendered", `${result.date}:${citySlug}`, {
          engine_result_date: result.date,
          city_slug: citySlug,
        });
      },
      purposeSelected(purpose) {
        emit("daily_timing_purpose_selected", "first-purpose", { purpose });
      },
      primaryCtaAudited(count, purpose) {
        emit("daily_timing_cta_audited", `${purpose ?? "control"}:${count}`, {
          purpose: purpose ?? "control",
          primary_cta_count: count,
        });
      },
    }),
    [emit]
  );

  return {
    arm,
    isTreatment: arm === DAILY_TIMING_TREATMENT_ARM,
    telemetry: arm ? telemetry : undefined,
  };
}
